import { isValidPhoneNumber } from 'libphonenumber-js';

// ── Phone validity ──────────────────────────────────────────────────
// Single source of truth for "can we SMS this person?" — guards against
// malformed numbers and non-Latin unicode that carriers reject.

export const hasValidPhone = (phoneDisplay: string | null | undefined): boolean => !!phoneDisplay && isValidPhoneNumber(phoneDisplay) && !/[\u0100-\uffff]/.test(phoneDisplay);

// ── Delivery-channel predicates ──────────────────────────────────────

export type DeliveryCheckable = Readonly<{
  hasEmailDeliveryMethodSelected?: boolean;
  hasTextDeliveryMethodSelected?: boolean;
}>;

export const isEmailRecipient = (p: DeliveryCheckable): boolean => !!p.hasEmailDeliveryMethodSelected;

export const isSmsRecipient = (p: DeliveryCheckable): boolean => !!p.hasTextDeliveryMethodSelected;

// ── Recompute per-person flags when the global delivery method changes ──

type PersonWithContact = DeliveryCheckable & Readonly<{ email?: string | null; phoneDisplay?: string | null; hasPhone?: boolean }>;

/**
 * Recomputes `hasEmailDeliveryMethodSelected` / `hasTextDeliveryMethodSelected`
 * for every person based on the current delivery method selection.
 *
 * Mirrors `applyChannelDefaults` from the PeoplePicker controller but operates
 * on the PeopleContext data shape rather than PeoplePicker view-items.
 */
export const recomputeDeliveryFlags = <T extends PersonWithContact>(people: ReadonlyArray<T>, deliveryMethod: 'text' | 'email' | 'both'): ReadonlyArray<T> =>
  people.map(person => {
    const hasPhone = hasValidPhone(person.phoneDisplay) || !!person.hasPhone;
    const hasEmail = !!person.email;

    switch (deliveryMethod) {
      case 'text':
        // Text-only: people without a phone are unreachable — no email fallback.
        return { ...person, hasTextDeliveryMethodSelected: hasPhone, hasEmailDeliveryMethodSelected: false };
      case 'email':
        // Email-only: people without an email are unreachable — no text fallback.
        return { ...person, hasEmailDeliveryMethodSelected: hasEmail, hasTextDeliveryMethodSelected: false };
      case 'both':
        return { ...person, hasTextDeliveryMethodSelected: hasPhone, hasEmailDeliveryMethodSelected: hasEmail };
    }
  });

// ── Session-persisted delivery method ────────────────────────────────

import { getSessionStorage } from '@shared/core/storage';

const VALID_METHODS = new Set(['text', 'email', 'both'] as const);
type ValidMethod = 'text' | 'email' | 'both';

function readPersisted(key: string): ValidMethod | undefined {
  try {
    const raw = getSessionStorage().getItem(key);
    if (raw && VALID_METHODS.has(raw as ValidMethod)) return raw as ValidMethod;
  } catch {
    // best-effort
  }
  return undefined;
}

function persist(key: string, value: ValidMethod): void {
  try {
    getSessionStorage().setItem(key, value);
  } catch {
    // best-effort
  }
}

export const readPersistedDeliveryMethod = (storageKey: string): ValidMethod => readPersisted(`deliveryMethod:${storageKey}`) ?? 'both';

export const persistDeliveryMethod = (storageKey: string, method: ValidMethod): void => persist(`deliveryMethod:${storageKey}`, method);

/** Read the persisted "if both are available, send via:" preference (separate from delivery method). */
export const readPersistedPreference = (storageKey: string): ValidMethod | undefined => readPersisted(`preference:${storageKey}`);

export const persistPreference = (storageKey: string, method: ValidMethod): void => persist(`preference:${storageKey}`, method);

/**
 * Recomputes per-person delivery flags when the "if both available" preference changes.
 * Uses fallback logic: the preferred channel is tried first; the other channel is used
 * as a fallback for guests who only have one contact method available.
 */
export const recomputeDeliveryFlagsWithPreference = <T extends PersonWithContact>(people: ReadonlyArray<T>, preference: 'text' | 'email' | 'both'): ReadonlyArray<T> =>
  people.map(person => {
    const hasPhone = hasValidPhone(person.phoneDisplay) || !!person.hasPhone;
    const hasEmail = !!person.email;
    switch (preference) {
      case 'text':
        return { ...person, hasTextDeliveryMethodSelected: hasPhone, hasEmailDeliveryMethodSelected: !hasPhone && hasEmail };
      case 'email':
        return { ...person, hasEmailDeliveryMethodSelected: hasEmail, hasTextDeliveryMethodSelected: !hasEmail && hasPhone };
      case 'both':
        return { ...person, hasTextDeliveryMethodSelected: hasPhone, hasEmailDeliveryMethodSelected: hasEmail };
    }
  });

export const splitByDeliveryChannel = <T extends DeliveryCheckable>(
  people: ReadonlyArray<T>,
): Readonly<{ emailRecipients: ReadonlyArray<T>; smsRecipients: ReadonlyArray<T> }> => ({
  emailRecipients: people.filter(isEmailRecipient),
  smsRecipients: people.filter(isSmsRecipient),
});
